import React, { useState, useMemo, useCallback } from "react";
import { toast } from "react-hot-toast";
import { APPS } from "./constants";
import { GET_IMAGE } from "../../helpers/images";
import { GET_CONNECTION_URL } from "../../api/integration";
import { GET_ZAPIER_AUTH_URL } from "../../api/integrations/zapier";
import { UseCompanyId } from "../../hooks/auth";

/** Normalize app for display: support both real (code, logo, metrics) and static (id, icon, description) shapes.
 * Resolve logo/icon via GET_IMAGE so API image keys become full URLs (same as CompanyMetrics page & SoftProfile). */
function toDisplayApp(app) {
  const rawIcon = app.logo || app.icon;
  const iconUrl = rawIcon ? GET_IMAGE(rawIcon) : null;
  if (app.code != null) {
    return {
      id: app.code || app._id,
      name: app.name || "App",
      icon: iconUrl,
      description: app.description || `Connect to ${app.name || "this app"} for metrics.`,
      _raw: app,
    };
  }
  return { ...app, id: app.id, icon: iconUrl, _raw: app };
}

/** Same connect flow as WorkspaceKpi ConnectedApplications / MetricsModal: OAuth, Apideck, Zapier redirect. */
function useConnectAccount(onSelect) {
  const companyId = UseCompanyId();
  return useCallback((app) => {
    if (!app) return;
    if (app.isConnected === true) return;
    if (app.isOauth || app.isApideck) {
      const connecting = toast.loading("Initializing connection ...");
      const params = app.isApideck
        ? { unifiedApi: app.type, serviceId: app.serviceId }
        : null;
      GET_CONNECTION_URL(app.code, params)
        .then(({ data: url }) => {
          if (url) {
            toast.success("Connecting...", { id: connecting });
            window.location.href = url;
          } else {
            toast.error("Unable to establish connection.", { id: connecting });
          }
        })
        .catch((err) => {
          toast.error(err.response?.data ?? "Server error", { id: connecting });
        });
    } else if (app.isZapier) {
      if (!companyId) {
        toast.error("Company context is required to connect Zapier.");
        return;
      }
      window.location.href = GET_ZAPIER_AUTH_URL(companyId);
    } else {
      // Manual / webhook / other: no redirect; open modal to view metrics
      onSelect?.(app);
    }
  }, [companyId, onSelect]);
}

function AppSelector({ apps: propApps, isLoading, onSelect, onCancel }) {
  const [searchQuery, setSearchQuery] = useState("");
  const connectAccount = useConnectAccount(onSelect);

  const sourceApps = useMemo(() => {
    if (Array.isArray(propApps) && propApps.length > 0) {
      return propApps.filter((a) => (a.code || a.id) !== "manual");
    }
    return APPS.filter((a) => a.id !== "manual");
  }, [propApps]);

  const displayApps = useMemo(() => sourceApps.map(toDisplayApp), [sourceApps]);

  const filteredApps = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return displayApps;
    return displayApps.filter(
      (app) =>
        (app.name || "").toLowerCase().includes(query) ||
        (app.description || "").toLowerCase().includes(query)
    );
  }, [searchQuery, displayApps]);

  let mainContent;
  if (isLoading) {
    mainContent = (
      <div className="flex flex-col items-center justify-center py-20 text-center animate-in fade-in duration-300">
        <div className="w-12 h-12 border-2 border-[var(--gmb-primary)] border-t-transparent rounded-full animate-spin mb-6" />
        <p className="text-gray-500 font-medium">Loading applications...</p>
      </div>
    );
  } else if (filteredApps.length === 0) {
    mainContent = (
      <div className="flex flex-col items-center justify-center py-20 text-center animate-in fade-in duration-300">
        <div className="w-16 h-16 bg-white text-gray-300 rounded-2xl flex items-center justify-center text-3xl mb-6 shadow-sm border border-gray-50">
          🔍
        </div>
        <h3 className="text-[18px] leading-[28px] font-semibold text-gray-900">No applications found</h3>
        <p className="text-gray-400 mt-2">Try searching with a different keyword or app name.</p>
        <button
          type="button"
          onClick={() => setSearchQuery("")}
          className="mt-8 text-sm font-semibold text-[var(--gmb-primary)] hover:text-[var(--gmb-primary-hover)] transition-colors"
        >
          Clear search
        </button>
      </div>
    );
  } else {
    mainContent = (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredApps.map((app) => (
          <button
            key={app.id}
            type="button"
            onClick={() => onSelect(app._raw != null ? app._raw : app)}
            className="bg-white border border-gray-100 p-8 rounded-2xl text-left hover:shadow-md hover:border-[var(--gmb-primary-tint)] transition-all group animate-in fade-in zoom-in-95 duration-300"
          >
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-8 border border-gray-100/50 shadow-sm transition-transform group-hover:scale-110 overflow-hidden bg-white p-2">
              {app.icon ? (
                <img src={app.icon} alt={`${app.name} logo`} className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; e.target.nextElementSibling?.classList.remove("hidden"); }} />
              ) : null}
              <span className={`w-full h-full flex items-center justify-center text-[22px] font-semibold text-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)] rounded-xl ${app.icon ? "hidden" : ""}`} aria-hidden>
                {(app.name || "A").charAt(0).toUpperCase()}
              </span>
            </div>
            <h3 className="text-[18px] leading-[28px] font-semibold text-gray-900 mb-2">{app.name}</h3>
            <p className="text-gray-500 text-sm leading-relaxed">{app.description}</p>
            <div className="mt-8 flex items-center text-[11px] leading-[16px] font-semibold text-gray-400 uppercase tracking-widest group-hover:text-[var(--gmb-primary)] transition-colors">
              <span>View metrics</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-3 h-3 ml-2 transition-transform group-hover:translate-x-1">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-12">
      <div className="bg-white border border-gray-100 rounded-[32px] shadow-sm overflow-hidden flex flex-col">
        <div className="p-8 md:p-12 border-b border-gray-50 bg-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h2 className="text-3xl leading-[38px] font-semibold text-gray-900">Connect a source</h2>
              <p className="text-gray-500 mt-1">Select an app to import metrics from your workspace.</p>
            </div>

            <div className="flex items-center gap-4">
              <div className="relative w-full md:w-80">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search applications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-100 rounded-2xl text-sm text-gray-900 focus:border-[var(--gmb-primary)] focus:ring-4 focus:ring-[var(--gmb-primary-soft)] outline-none transition-all shadow-sm"
                />
              </div>
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2.5 rounded-2xl text-sm font-semibold text-gray-500 hover:bg-gray-100 transition-all whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        <div className="p-8 md:p-12 bg-gray-50/30 min-h-[400px]">
          {mainContent}
        </div>
      </div>
    </div>
  );
}

export default AppSelector;
